import React from 'react';
import { UserProfile, OAS_DELAY_STRATEGIES, CPP_DELAY_STRATEGIES } from '../../types/advanced-retirement';
import FormattedInput from '../FormattedInput';

interface GovernmentBenefitsSectionProps {
  userProfile: UserProfile;
  handleInputChange: (field: keyof UserProfile, value: any) => void;
}

// The optimizer only chooses the primary's benefit ages; the spouse's dropdowns offer fixed ages only
const isFixedAge = (strategy: string) => strategy !== 'Calculate Suggested Strategy';

const GovernmentBenefitsSection: React.FC<GovernmentBenefitsSectionProps> = ({
  userProfile,
  handleInputChange
}) => {
  return (
    <div className="form-section">
      <h3>Government Benefits</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>
            CPP Benefit{' '}
            <a
              href="https://www.canada.ca/en/services/benefits/publicpensions/cpp/cpp-benefit/amount.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.8em', fontWeight: 'normal' }}
            >
              (Don't know? Check Service Canada)
            </a>
          </label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>My CPP</small>
              <FormattedInput
                defaultText="Default: $15,000"
                value={userProfile.cppBenefit}
                onChange={(value) => handleInputChange('cppBenefit', value)}
                min={0}
                step={1000}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse CPP</small>
                <FormattedInput
                  defaultText="Default: $12,000"
                  value={userProfile.spouseCppBenefit || 0}
                  onChange={(value) => handleInputChange('spouseCppBenefit', value)}
                  min={0}
                  step={1000}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>OAS Benefit</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>My OAS</small>
              <FormattedInput
                defaultText="Default: $8,000"
                value={userProfile.oasBenefit}
                onChange={(value) => handleInputChange('oasBenefit', value)}
                min={0}
                step={1000}
                formatOptions={{ type: 'currency', decimalPlaces: 0 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse OAS</small>
                <FormattedInput
                  defaultText="Default: $8,000"
                  value={userProfile.spouseOasBenefit || 0}
                  onChange={(value) => handleInputChange('spouseOasBenefit', value)}
                  min={0}
                  step={1000}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>OAS Delay Strategy</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>My Strategy</small>
              <select 
                value={userProfile.oasDelayStrategy}
                onChange={(e) => handleInputChange('oasDelayStrategy', e.target.value)}
              >
                {OAS_DELAY_STRATEGIES.map(strategy => (
                  <option key={strategy} value={strategy}>{strategy}</option>
                ))}
              </select>
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Strategy</small>
                <select 
                  value={userProfile.spouseOasDelayStrategy || 'Start at 65'}
                  onChange={(e) => handleInputChange('spouseOasDelayStrategy', e.target.value)}
                >
                  {OAS_DELAY_STRATEGIES.filter(isFixedAge).map(strategy => (
                    <option key={strategy} value={strategy}>{strategy}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>CPP Delay Strategy</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>My Strategy</small>
              <select
                value={userProfile.cppDelayStrategy}
                onChange={(e) => handleInputChange('cppDelayStrategy', e.target.value)}
              >
                {CPP_DELAY_STRATEGIES.map(strategy => (
                  <option key={strategy} value={strategy}>{strategy}</option>
                ))}
              </select>
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Strategy</small>
                <select
                  value={userProfile.spouseCppDelayStrategy || 'Start at 65'}
                  onChange={(e) => handleInputChange('spouseCppDelayStrategy', e.target.value)}
                >
                  {CPP_DELAY_STRATEGIES.filter(isFixedAge).map(strategy => (
                    <option key={strategy} value={strategy}>{strategy}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {userProfile.spouseAge && (
          <div className="form-group">
            <label>CPP Survivor Benefit (Monthly)</label>
            <div className="side-by-side-inputs">
              <div className="input-group">
                <small>My Survivor Benefit</small>
                <FormattedInput
                  defaultText="Default: $0"
                  value={userProfile.cppSurvivorBenefit || 0}
                  onChange={(value) => handleInputChange('cppSurvivorBenefit', value)}
                  min={0}
                  step={100}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
              <div className="input-group">
                <small>Spouse Survivor Benefit</small>
                <FormattedInput
                  defaultText="Default: $0"
                  value={userProfile.spouseCppSurvivorBenefit || 0}
                  onChange={(value) => handleInputChange('spouseCppSurvivorBenefit', value)}
                  min={0}
                  step={100}
                  formatOptions={{ type: 'currency', decimalPlaces: 0 }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovernmentBenefitsSection; 